import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, Plus, Trash2 } from "lucide-react";
import { http, formatSoles } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Confirm, Pill, QUOTE_STATUS, quoteStatus, fmtDateTime, fmtDate, exportXlsx } from "./ui";

export default function QuotesPage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState([]);
  const load = () => http.get("/quotes").then((r) => setQuotes(r.data));
  useEffect(() => { load(); }, []);

  const list = useMemo(() => quotes.filter((x) => {
    const t = q.toLowerCase().trim();
    if (t && !`${x.quote_code || ""} ${x.name} ${x.whatsapp}`.toLowerCase().includes(t)) return false;
    if (status && quoteStatus(x.status).value !== status) return false;
    return true;
  }), [quotes, q, status]);

  async function create() {
    const { data } = await http.post("/quotes/manual", { name: "", whatsapp: "", customer_type: "", status: "pendiente" });
    navigate(`/admin/cotizaciones/${data.id}`);
  }
  async function bulk(action, value) {
    await http.post("/quotes/bulk", { ids: sel, action, value });
    toast.success("Acción aplicada");
    setSel([]);
    load();
  }
  const allSel = list.length > 0 && list.every((x) => sel.includes(x.id));
  const exportRows = () => exportXlsx(list.map((x) => ({
    Código: x.quote_code || "", Fecha: fmtDateTime(x.created_at), Cliente: x.name, WhatsApp: x.whatsapp, Tipo: x.customer_type || "",
    "Fecha evento": x.event_date || "", Lugar: x.district || "", Total: x.total || 0, Estado: quoteStatus(x.status).label, Pedido: x.order_code || "",
  })), "cotizaciones-floral-rosse", "Cotizaciones");

  return (
    <div data-testid="admin-quotes-page">
      <PageHeader title="Cotizaciones" subtitle={`${quotes.length} cotizaciones`}
        actions={<>
          <Button variant="outline" onClick={exportRows} data-testid="export-quotes"><Download size={14} className="mr-1" /> Excel</Button>
          <Button className="btn-primary" onClick={create} data-testid="new-quote-btn"><Plus size={14} className="mr-1" /> Nueva cotización</Button>
        </>} />
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código, nombre o WhatsApp" className="pl-9 bg-white" data-testid="quote-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-input px-3 text-sm bg-white" data-testid="quote-filter-status">
          <option value="">Todos los estados</option>
          {QUOTE_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4 bg-white rounded-xl border border-black/5 p-2">
        <Checkbox checked={allSel} onCheckedChange={(v) => setSel(v ? list.map((x) => x.id) : [])} data-testid="select-all-quotes" />
        <span className="text-sm font-semibold mr-2">{sel.length} seleccionadas</span>
        {sel.length > 0 && <>
          <select defaultValue="" onChange={(e) => e.target.value && bulk("status", e.target.value)} className="h-8 rounded-md border border-input px-2 text-xs bg-white" data-testid="bulk-quote-status">
            <option value="">Marcar como…</option>
            {QUOTE_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <Confirm onConfirm={() => bulk("delete")} title={`¿Eliminar ${sel.length} cotizaciones?`} trigger={<Button size="sm" variant="outline" className="text-red-600"><Trash2 size={12} className="mr-1" /> Eliminar</Button>} />
        </>}
      </div>
      <div className="bg-white rounded-2xl border border-black/5 divide-y">
        {list.length === 0 && <p className="text-sm text-neutral-500 p-8 text-center">No hay cotizaciones.</p>}
        {list.map((x) => {
          const st = quoteStatus(x.status);
          return (
            <div key={x.id} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-neutral-50" data-testid={`quote-row-${x.id}`}>
              <Checkbox checked={sel.includes(x.id)} onCheckedChange={(v) => setSel((s) => (v ? [...s, x.id] : s.filter((y) => y !== x.id)))} />
              <Link to={`/admin/cotizaciones/${x.id}`} className="flex-1 grid grid-cols-2 sm:grid-cols-6 gap-2 items-center" data-testid={`open-quote-${x.id}`}>
                <div className="font-serif text-lg font-bold text-[var(--brand-accent)]">{x.quote_code || "COT"}</div>
                <div className="sm:col-span-2 min-w-0">
                  <div className="font-semibold truncate">{x.name || "(sin nombre)"}</div>
                  <div className="text-xs text-neutral-500">{x.whatsapp} {x.customer_type ? `· ${x.customer_type}` : ""}</div>
                </div>
                <div className="text-xs text-neutral-500">{fmtDateTime(x.created_at)}{x.event_date && <div>Evento: {fmtDate(x.event_date)}</div>}</div>
                <div className="font-semibold">{x.total ? formatSoles(x.total) : "—"}</div>
                <div className="flex gap-1 flex-wrap">
                  <Pill className={st.cls}>{st.label}</Pill>
                  {x.order_code && <Pill className="bg-neutral-100 text-neutral-700">{x.order_code}</Pill>}
                  {x.source === "manual" && <Pill className="bg-neutral-100 text-neutral-500">Manual</Pill>}
                </div>
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}
